import type { intlShape } from 'react-intl';
import type {
	IProductHeader,
	IDistributionCenterPriorityOverride,
} from './values';
import type { MetaProductTagLink, MetaProductType } from './ogm-hub';

export type { ICustomer } from './remote/hubs/Web_Controllers_V2_CustomersController';

export interface ISupplier {
	id: number;
	name: string;
	dssiCode: string;
}

export interface IBlueprint {
	id: number;
	name: string;
	metaProductCount: number;
	locationCount: number;
	distributionCenterCount: number;
	createdAt: string;
	updatedAt: string;
	supplier?: ISupplier;
	supplierId?: number;
	supplierProvidesRTA: boolean;
	supplierTaggingEnabled: boolean;
}

export interface IFullLocation {
	customerId: number;
	name: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	state: string;
	zip: string;
	externalCode: string;
	id: number;
	locationCode: string;
}

export interface ILocation {
	id: number;
	name: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	state: string;
	zip: string;
	locationCode: string;
	externalCode: string;
	distributionCenterId: number;
}

export interface IEnabledLocation {
	id: number;
	name: string;
	bidlistDescription: string;
	overrideDescription: string;
	restrictionDescription: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	state: string;
	zip: string;
	initial: boolean;
	enabled: boolean;
	locationCode: string;
	externalCode: string;
	distributionCenterExternalCode: string;
	distributionCenterName: string;
}

export interface IDistributionCenter {
	id: number;
	name: string;
	externalCode: string;
}

export interface IDistributionCenterSelfService {
	id: number;
	name: string;
	externalCode: string;
	dssiCode: string;
}

export interface IProductWithUM {
	corpSupplierCode: string;
	supplierProductNumber: string;
	unitMeasureCode: string;
	specIndicatorId?: number;
	exists?: boolean;
}

export interface IProductListingData extends IProductWithUM {
	description: string;
	specIndicatorId?: number;
	brand: string;
	packSize: string;
}

export interface IMetaProduct {
	id: number;
	name: string;
	search: string;
	itemCount: number;
	locationAvailability: number;
	distributionCenterAvailability: number;
	products?: IProductWithUM[];
	productHeaders?: IProductHeader[];
	distributionCenterOverrides?: IDistributionCenterPriorityOverride[];
	isSelectByUm: boolean;
	umSelectionModeId: UmSelectionMode;
	maintainedBy: number;
	statusId: number;
	selectionRuleSetId: number;
	pendingRebuild: boolean;
	specIndicatorId?: number;
	customCategoryId?: number;
	coverageRatio: string;
	tagLinks: MetaProductTagLink[];
	typeId?: MetaProductType;
	isOnMenu?: boolean;
}

export interface IProductRestriction {
	id: number;
	supplierId: number;
	supplierName: string;
	supplierProductNumber: string;
	umCodes: string[];
	restrictionMessage: string;
}

export interface IProductIdentifier {
	corpSupplierCode: string;
	supplierProductNumber: string;
}

export interface IUnitOfMeasurePrice {
	distributionCenterName: string;
	distributionCenter: string;
	pricePerUnitDivisor: string;
	locationNumber: string;
	unitOfMeasure: string;
	price: string;
	pricePerPound: string;
	pricePerUnitUM: string;
	isBranded: boolean;
}

export interface IProductPrices extends IProductIdentifier {
	manufacturerProductNumbers: Record<string, string>;
	casePrices: IUnitOfMeasurePrice[];
	highCasePrice: number;
	highCaseCostPerUnitDivisor: number;
	lowCasePrice: number;
	lowCaseCostPerUnitDivisor: number;
	casePricePerUnitUM: string;
	splitPrices: IUnitOfMeasurePrice[];
	highSplitPrice: number;
	highSplitCostPerUnitDivisor: number;
	lowSplitPrice: number;
	lowSplitCostPerUnitDivisor: number;
	splitPricePerUnitUM: string;
}

export type IProductPricesMap = Record<string, IProductPrices>;

export interface IProductDetails {
	productNumber: string;
	thirdLineDescription: string;
	product: IProductHeader;
	images: string[];
	gtin: string;
}

export interface INutritionalInformation {
	calories: number;
	carbohydratesInGrams: number;
	cholesterolInMg: number;
	fatInGrams: number;
	fiberInGrams: number;
	proteinInGrams: number;
	saturatedFatInGrams: number;
	servingPerContainer: number;
	servingUnitQuantity: number;
	servingUnitSize: number;
	sodiumInMg: number;
	sugarInGrams: number;
	transFatInGrams: number;
	calciumInMg: number;
	caloriesFromFat: number;
	disclaimer: string;
	ingredientList: string;
	ironInMg: number;
	monounsaturatedFatInGrams: number;
	potassiumInMg: number;
	vitaminDInMcg: number;
	additionalInfo: IAdditionalNutritionInfo[];
}

export interface IAdditionalNutritionInfo {
	key: string;
	val: number;
	unit?: string;
	dailyValuePercent?: number;
}

export interface IMetaProductItem extends IProductIdentifier {
	id: number;
	description: string;
	proprietary?: string[];
	extendedDescription: string;
	brand: string;
	packSize: string;
	activeDistributionCenters: string[];
	coveredDistributionCenters: string[];
	price: string;
	productPrices?: IProductPrices;
	proprietaryEligibleDistributionCenters?: string[];
	supplierProductNumber: string;
	demandStatus: string[];
	remoteStock: string[];
	brandFlags?: string[];
	traits?: string[];
	categoryLevel1: string;
	categoryLevel2: string;
	categoryLevel3: string;
	categoryLevel4: string;
	categoryLevel1Code: string;
	categoryLevel2Code: string;
	categoryLevel3Code: string;
	categoryLevel4Code: string;
	conversionQuantity: number;
	itemOrder: number;
}

export interface IMetaProductItemRemoval {
	metaProductId: number;
	metaProductItem: IProductWithUM;
	metaProductItemHeader: IProductHeader;
}

export interface IMetaProductItemSortRow extends IMetaProductItem {
	distributionCenterCount: number;
}

export interface IOrderGuideLine {
	productNumber: string;
	blueprintName: string;
	metaProductName: string;
	productDescription: string;
	brand: string;
	packSize: string;
	distributionCenterExternalCode: string;
	distributionCenterName: string;
	unitOfMeasure: string;
}

export interface ICoverageAlert {
	blueprintName: string;
	metaProductName: string;
	blueprintId: number;
	metaProductId: number;
	type: number;
	severity: string;
	locationCount: number;
	distributionCenterCount: number;
	locationAvailability: number;
	distributionCenterAvailability: number;
}

export interface IProductAlert {
	id: number;
	blueprintId: number;
	blueprintName: string;
	metaProductId: number;
	metaProductName: string;
	createdAt: Date;
	alertType: ProductAlertType;
}

export interface IDiscontinuedProductAlert extends IProductAlert {
	inactivatedAt: Date;
	productNumber: string;
	dssiSupplierCode: string;
	distributionCenterDssiCode: string;
	distributionCenterName: string;
}

export enum ProductAlertType {
	Discontinued = 1,
	Coverage = 2,
}

export enum SpecIndicator {
	Unassigned = 0,
	Hard = 1,
	Required = 2,
	Open = 3,
}

export enum selectByUmFilterType {
	Enabled = 0,
	Disabled = 1,
}

export enum UmSelectionMode {
	Off = 1,
	EachOnly = 2,
}

export enum AvailabilityResultState {
	None = 0,
	Partial = 1,
	Full = 2,
}

export enum OgmAiOptimizationDisplayType {
	Optimizations = 0,
	Groupings = 1,
}

export interface ICustomCategory {
	id: number;
	customerId: number;
	category: string;
	categorySequence: number;
	isDefault: boolean;
	pendingDelete: boolean;
}

export interface IUser {
	id: number;
	username: string;
	firstName: string;
	lastName: string;
	roles: string[];
	userProfileId: number;
}

export interface IUserProfile {
	id: number;
	description: string;
}

export interface ISelectionRuleSet {
	id: number;
	name: string;
}

export interface IMetaProductListingFilterCriteria {
	coverageFilterFlag: boolean;
	phaseOutFilterFlag: boolean;
	metaProductHardSpecIndicatorFilterFlag: boolean;
	metaProductRequiredSpecIndicatorFilterFlag: boolean;
	metaProductOpenSpecIndicatorFilterFlag: boolean;
	metaProductUnassignedSpecIndicatorFilterFlag: boolean;
	metaProductOffAiSettingFilterFlag: boolean;
	metaProductManualAiSettingFilterFlag: boolean;
	metaProductAutoAiSettingFilterFlag: boolean;
	metaProductAutoReprioritizeAiSettingFilterFlag: boolean;
	metaProductIsSelectByUmEnabledFilterFlag: boolean;
	metaProductIsSelectByUmDisabledFilterFlag: boolean;
	metaProductItemHardSpecIndicatorFilterFlag: boolean;
	metaProductItemRequiredSpecIndicatorFilterFlag: boolean;
	metaProductItemOpenSpecIndicatorFilterFlag: boolean;
	metaProductItemUnassignedSpecIndicatorFilterFlag: boolean;
	metaProductDynamicMasterItemFilterFlag: boolean;
	metaProductManualMasterItemFilterFlag: boolean;
	metaproductIsOnMenuFilterFlag: boolean;
	filterText?: string;
	customCategoryIds: number[];
	categoryTagId: number | null;
	primaryTagId: number | null;
	packagingTagId: number | null;
	generalTagIds: number[];
}

/**
 * @deprecated do not use, prefer `PageProps<'slice'>`
 */
export interface IConnectedComponentProps {
	dispatch: (...args: any[]) => any;
	intl: intlShape;
}
